import type { ComponentProps, ReactNode } from 'react';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import { Icon } from '../../assets/Icon/Icon';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Caption } from '../../text/Caption';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { Port } from '../Port/Port';
import {
  type PortSpeed,
  type PortStatus,
  getPortIcon,
  getPortSpeedLabel,
  getPortStatusLabel,
} from '../Port/utils';

const legendDividerWidth = '$2';

const LegendIcon = styled(Icon, {
  color: '$$legendIconColor',
});
LegendIcon.displayName = 'LegendIcon';

const LegendLabel = styled(Text, {
  color: '$$legendLabelColor',
  whiteSpace: 'nowrap',
});
LegendLabel.displayName = 'LegendLabel';

const LegendContainer = styled(AlignStack, {
  position: 'relative',
  zIndex: 2,
  width: '$$legendWidth',
  minWidth: 0,
  padding: '$2 $6',
  borderRadius: '$6',
  cursor: 'pointer',

  variants: {
    direction: {
      column: {},
      row: {
        '&::after': {
          content: '',
          position: 'absolute',
          top: '$3',
          right: '-$4',
          bottom: '$3',
          transform: 'translateX(50%)',
          width: legendDividerWidth,
          backgroundColor: colors.strokeNeutralLight,
          borderRadius: '99em',

          [darkThemeSelector]: {
            backgroundColor: colors.strokeNeutralDark,
          },
        },

        '&:last-child::after': {
          display: 'none',
        },
      },
    },
    isPressable: {
      true: {
        [selectors.hover]: {
          $$legendLabelColor: colors.headingNeutralLight,
          $$legendIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$legendLabelColor: colors.headingNeutralDark,
            $$legendIconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
          outline: 'none',

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {},
    },
    selected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$legendLabelColor: colors.headingBrandLight,
          $$legendIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$legendLabelColor: colors.headingBrandDark,
            $$legendIconColor: colors.bodyBrandDark,
          },
        },
      },
      false: {},
    },
  },
});
LegendContainer.displayName = 'LegendContainer';

export type LegendProps = {
  blocking?: boolean;
  collapsed?: boolean;
  direction?: 'column' | 'row';
  error?: boolean;
  icon?: IconName;
  label?: React.ReactNode;
  onClick?: (event: any) => void;
  poe?: boolean;
  selected?: boolean;
  speed?: PortSpeed;
  status?: PortStatus;
  uplink?: boolean;
  variant?: VariantProp;
};

export function Legend({
  blocking,
  collapsed,
  direction,
  error,
  icon,
  label,
  onClick,
  poe,
  selected,
  speed,
  status,
  uplink,
  variant,
  ...remaining
}: LegendProps) {
  const portIcon = status ? getPortIcon(uplink, blocking, poe) : icon;
  const displayLabel =
    label ||
    (speed && status ? `${getPortSpeedLabel(speed)} ${getPortStatusLabel(status)}` : undefined);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  const portLegendRender = (
    <LegendContainer
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick && 'button'}
      tabIndex={onClick ? 0 : undefined}
      isPressable={!!onClick}
      direction={direction}
      align="center"
      gap={6}
      preset="small"
      selected={selected}
      start={
        portIcon ? (
          <LegendIcon icon={portIcon} size={12} />
        ) : (
          <Port
            port="ethernet"
            negotiatedPortSpeed={speed}
            status={status}
            uplink={uplink}
            blocking={blocking}
            poe={poe}
            mode="icon"
          />
        )
      }
      {...remaining}
    >
      {!collapsed && <LegendLabel>{displayLabel}</LegendLabel>}
    </LegendContainer>
  );

  if (collapsed) {
    return (
      <Tooltip align="center" side="left" sideOffset={6} toggle={false} contents={displayLabel}>
        {portLegendRender}
      </Tooltip>
    );
  }

  return portLegendRender;
}

const LegendsContainer = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
      },
      row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '$8',
      },
    },
  },
});
LegendsContainer.displayName = 'LegendsContainer';

const LegendsGroupLabel = styled(Caption, {
  color: colors.disabledNeutralLight,

  [darkThemeSelector]: {
    color: colors.disabledNeutralDark,
  },
});
LegendsGroupLabel.displayName = 'LegendsGroupLabel';

const LegendsGroupsGroup = styled(AlignStack, {
  display: 'flex',
  minWidth: 'auto',
});
LegendsGroupsGroup.displayName = 'LegendsGroupsGroup';

const LegendsGroupsContainer = styled('div', {
  display: 'flex',
  gap: '$12',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
        $$legendWidth: '100%',
      },
      row: {
        flexDirection: 'row',
      },
    },
  },
});
LegendsGroupsContainer.displayName = 'LegendsGroupsContainer';

export type LegendsGroupProps = {
  group: ReactNode;
  legends: LegendProps[];
};

export type LegendsProps = {
  /**
   * Allow for collapsed legends.
   */
  collapsed?: boolean;
  /**
   * Allow for the direction of the legends.
   */
  direction?: 'row' | 'column';
  /**
   * Allow for grouped legends.
   */
  grouped?: LegendsGroupProps[];
  /**
   * Allow for regular legends.
   */
  legends?: LegendProps[];
} & ComponentProps<typeof LegendsContainer>;

export function Legends({
  collapsed,
  direction = 'row',
  grouped,
  legends,
  ...remaining
}: LegendsProps) {
  if (grouped && grouped.length > 0) {
    return (
      <LegendsGroupsContainer direction={direction} {...remaining}>
        {grouped.map((group) => (
          <LegendsGroupsGroup
            key={`group-${group.group}`}
            align="center"
            direction={direction}
            gap={direction === 'row' ? 4 : 0}
            preset="small"
            width="fit-content"
            start={<LegendsGroupLabel>{group.group}</LegendsGroupLabel>}
          >
            <LegendsContainer direction={direction}>
              {group.legends.map((legend) => (
                <Legend
                  key={`legend-${group.group}-${legend.label}`}
                  {...legend}
                  collapsed={collapsed}
                  direction={direction}
                />
              ))}
            </LegendsContainer>
          </LegendsGroupsGroup>
        ))}
      </LegendsGroupsContainer>
    );
  }

  return (
    <LegendsContainer direction={direction} {...remaining}>
      {legends?.map((legend) => (
        <Legend
          key={`legend-${legend.label}`}
          {...legend}
          collapsed={collapsed}
          direction={direction}
        />
      ))}
    </LegendsContainer>
  );
}
